import React from 'react'
import { 
  Code2, Plus, Trash2, Copy, CheckCircle, AlertCircle, 
  Activity, Users, Webhook 
} from 'lucide-react'

export default function PartnersTab({ 
  partners, 
  copiedField, 
  copyToClipboard, 
  handleDeactivate, 
  handleAddSeats 
}) {
  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard 
          icon={Users}
          iconBg="bg-green-500/100/20"
          iconColor="text-green-500"
          value={partners.length}
          label="Total Partners"
        />
        <StatCard 
          icon={CheckCircle}
          iconBg="bg-green-500/20"
          iconColor="text-green-600"
          value={partners.filter(p => p.status === 'active').length}
          label="Active"
        />
        <StatCard 
          icon={Activity}
          iconBg="bg-blue-500/20"
          iconColor="text-blue-400"
          value={partners.reduce((sum, p) => sum + (p.api_calls_count || 0), 0).toLocaleString()}
          label="Total API Calls"
        />
        <StatCard 
          icon={Webhook}
          iconBg="bg-purple-500/20"
          iconColor="text-purple-400"
          value={partners.filter(p => p.webhook_url).length}
          label="Webhooks Configured"
        />
      </div>

      {/* Partners List */}
      <div className="bg-zinc-800 rounded-xl border border-zinc-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-zinc-900 border-b border-zinc-700">
              <tr>
                <th className="text-left px-6 py-3 text-sm font-medium text-zinc-400">Partner</th>
                <th className="text-left px-6 py-3 text-sm font-medium text-zinc-400">Client ID</th>
                <th className="text-left px-6 py-3 text-sm font-medium text-zinc-400">Status</th>
                <th className="text-left px-6 py-3 text-sm font-medium text-zinc-400">Seats</th>
                <th className="text-left px-6 py-3 text-sm font-medium text-zinc-400">API Calls</th>
                <th className="text-left px-6 py-3 text-sm font-medium text-zinc-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {partners.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-zinc-400">
                    <Code2 className="w-12 h-12 mx-auto mb-3 text-zinc-500" />
                    <p className="font-medium">No integration partners yet</p>
                    <p className="text-sm mt-1">Create your first partner to get started</p>
                  </td>
                </tr>
              ) : (
                partners.map((partner) => (
                  <PartnerRow 
                    key={partner.partner_id}
                    partner={partner}
                    copiedField={copiedField}
                    copyToClipboard={copyToClipboard}
                    handleDeactivate={handleDeactivate}
                    handleAddSeats={handleAddSeats}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function StatCard({ icon: Icon, iconBg, iconColor, value, label }) {
  return (
    <div className="bg-zinc-800 rounded-xl p-4 border border-zinc-700">
      <div className="flex items-center gap-3">
        <div className={`p-2 ${iconBg} rounded-lg`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        <div>
          <p className="text-2xl font-bold">{value}</p>
          <p className="text-sm text-zinc-400">{label}</p>
        </div>
      </div>
    </div>
  )
}

function PartnerRow({ partner, copiedField, copyToClipboard, handleDeactivate, handleAddSeats }) {
  return (
    <tr className="hover:bg-zinc-900">
      <td className="px-6 py-4">
        <div>
          <p className="font-medium text-white">{partner.name}</p>
          <p className="text-sm text-zinc-400">{partner.contact_email}</p>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <code className="text-xs bg-zinc-800 px-2 py-1 rounded font-mono">
            {partner.client_id?.slice(0, 20)}...
          </code>
          <button
            onClick={() => copyToClipboard(partner.client_id, partner.partner_id)}
            className="text-zinc-500 hover:text-zinc-300"
          >
            {copiedField === partner.partner_id ? (
              <CheckCircle className="w-4 h-4 text-green-500" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </button>
        </div>
      </td>
      <td className="px-6 py-4">
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
          partner.status === 'active' 
            ? 'bg-green-500/20 text-green-400' 
            : 'bg-red-500/20 text-red-400'
        }`}>
          {partner.status === 'active' ? (
            <CheckCircle className="w-3 h-3" />
          ) : (
            <AlertCircle className="w-3 h-3" />
          )}
          {partner.status}
        </span>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
            (partner.available_seats || 0) > 0 
              ? 'bg-green-500/20 text-green-400' 
              : 'bg-red-500/20 text-red-400'
          }`}>
            {partner.available_seats || 0} avail
          </span>
          <span className="text-xs text-zinc-400">
            / {partner.used_seats || 0} used
          </span>
        </div>
      </td>
      <td className="px-6 py-4 text-zinc-300">
        {(partner.api_calls_count || 0).toLocaleString()}
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          {partner.status === 'active' && (
            <>
              <button
                onClick={() => handleAddSeats(partner.partner_id, partner.name)}
                className="text-green-500 hover:text-indigo-800"
                title="Add Seats"
              >
                <Plus className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDeactivate(partner.partner_id, partner.name)}
                className="text-red-400 hover:text-red-800"
                title="Deactivate"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  )
}
